// Card evento pubblica condivisa (Variante B: biglietto verticale con strappo
// perforato) usata sia in Home sia nelle vetrine partner. Include il badge
// "conto alla rovescia" per gli eventi imminenti.
// NB: chi la usa deve prima chiamare setCategories(...) per risolvere label/colori.
import React from 'react';
import { Link } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { categoryMeta, catColor, euro, EVENTS_HERO_IMG } from '@/lib/eventsMock';
import { MapPin, Ticket, Clock } from 'lucide-react';

// Giorni mancanti (0 = oggi). null se data assente/non valida.
export function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const b = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((b - a) / 86400000);
}

function countdownLabel(n) {
  if (n === null || n < 0 || n > 30) return null;
  if (n === 0) return 'Oggi';
  if (n === 1) return 'Domani';
  return `tra ${n} giorni`;
}

export function PublicEventCard({ e }) {
  const img = e.cover ? mediaUrl(e.cover) : EVENTS_HERO_IMG;
  const meta = categoryMeta(e.category);
  const d = e.date ? new Date(e.date) : null;
  const valid = d && !Number.isNaN(d.getTime());
  const day = valid ? String(d.getDate()).padStart(2, '0') : '--';
  const month = valid ? d.toLocaleDateString('it-IT', { month: 'short' }).toUpperCase().replace('.', '') : '';
  const cd = countdownLabel(daysUntil(e.date));

  return (
    <Link to={`/eventi/${e.slug || e.id}`} className="group block h-full" data-testid={`public-event-card-${e.slug || e.id}`}>
      <div className="flex h-full flex-col overflow-hidden rounded-2xl bg-card shadow-[var(--shadow-sm)] ring-1 ring-border transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-1 hover:shadow-[var(--shadow-md)]">
        <div className="relative h-40 w-full overflow-hidden">
          <img loading="lazy" src={img} alt={e.title} onError={(ev) => { ev.currentTarget.src = EVENTS_HERO_IMG; }}
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
          <span className="absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-semibold text-white shadow" style={{ backgroundColor: catColor(e.category) }}>{meta.label}</span>
          <span className="absolute right-3 top-3 flex flex-col items-center rounded-lg bg-background/95 px-2 py-1 text-center leading-none shadow">
            <span className="font-display text-lg font-extrabold text-foreground">{day}</span>
            <span className="text-[10px] font-semibold tracking-wider text-muted-foreground">{month}</span>
          </span>
          {cd && (
            <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--brand-sun))] px-2.5 py-1 text-xs font-bold text-[#1c2128] shadow" data-testid="event-countdown-badge">
              <Clock className="h-3.5 w-3.5" /> {cd}
            </span>
          )}
        </div>
        <div className="relative h-0">
          <div className="absolute inset-x-4 top-0 border-t-2 border-dashed border-border" />
          <div className="absolute -left-2 -top-2 h-4 w-4 rounded-full bg-background" />
          <div className="absolute -right-2 -top-2 h-4 w-4 rounded-full bg-background" />
        </div>
        <div className="flex flex-1 flex-col p-4 pt-5">
          <h3 className="truncate font-display text-lg font-bold text-foreground transition-colors group-hover:text-primary">{e.title}</h3>
          <p className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5 shrink-0" /> {e.location || 'Sila'}{e.time ? ` \u00b7 ${e.time}` : ''}</p>
          <div className="mt-3 flex items-center justify-between pt-1">
            <span className="text-base font-bold text-foreground">{e.price_from > 0 ? `da ${euro(e.price_from)}` : 'Gratis'}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-transform duration-200 group-hover:translate-x-0.5">
              <Ticket className="h-3.5 w-3.5" /> Biglietti
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
